import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import {
	CircleDot,
	CircleCheck,
	FolderOpen,
	FolderPlus,
	Layers,
	RotateCcw,
	UserMinus,
	UserRound
} from "lucide-react"
import { api } from "../api/client"
import { useApp } from "../state/AppState"
import { accent } from "../design/palette"
import { ProgressChart } from "../components/charts/ProgressChart"
import { SplitBar, StatTile } from "../components/charts/StatTile"
import { Avatar, Button, Card, EmptyState, SectionHeading, Select, Spinner, cx } from "../components/ui"
import { plural, relativeTime } from "../lib/format"
import { NewProjectDialog } from "./NewProjectDialog"

const WINDOWS = [
	{ value: 14, label: "Last 14 days" },
	{ value: 30, label: "Last 30 days" },
	{ value: 90, label: "Last 90 days" }
]

export function DashboardPage() {
	const { projects, toast, timezone, user } = useApp()
	const [days, setDays] = useState(30)
	const [data, setData] = useState(null)
	const [loading, setLoading] = useState(true)
	const [creating, setCreating] = useState(false)

	useEffect(() => {
		let cancelled = false
		setLoading(true)
		api
			.dashboard(days)
			.then((result) => {
				if (!cancelled) setData(result)
			})
			.catch((error) => toast(error.message, "error"))
			.finally(() => {
				if (!cancelled) setLoading(false)
			})
		return () => {
			cancelled = true
		}
	}, [days, projects, toast])

	const counters = data?.counters

	return (
		<div className="space-y-6">
			<div className="flex flex-wrap items-end justify-between gap-3">
				<div>
					<h1 className="text-xl font-semibold tracking-tight text-ink-900">
						Dashboard
					</h1>
				</div>
				<div className="flex items-center gap-2">
					<Select
						value={days}
						onChange={(event) => setDays(Number(event.target.value))}
						className="w-40"
						aria-label="Chart window"
					>
						{WINDOWS.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</Select>
					<Button variant="primary" icon={FolderPlus} onClick={() => setCreating(true)}>
						New project
					</Button>
				</div>
			</div>

			{loading && !data ? (
				<Spinner />
			) : !counters ? null : (
				<>
					<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
						<StatTile
							label="Open"
							value={counters.open}
							tone="open"
							icon={CircleDot}
							hint="Still waiting on an answer"
						/>
						<StatTile
							label="Resolved"
							value={counters.closed}
							tone="closed"
							icon={CircleCheck}
							hint={`${counters.resolution_rate}% of everything raised`}
						/>
						<StatTile
							label={counters.assigned_to_me ? "Waiting on you" : "Total issues"}
							value={counters.assigned_to_me || counters.total}
							tone={counters.assigned_to_me ? "open" : "neutral"}
							icon={Layers}
							hint={
								counters.assigned_to_me
									? `${plural(counters.total, "issue")} in total`
									: `Across ${plural(counters.projects, "project")}`
							}
						/>
						<StatTile
							label="Active projects"
							value={counters.active_projects}
							tone="brand"
							icon={FolderOpen}
							hint="Not yet archived"
						/>
					</div>

					<Card className="p-4 sm:p-5">
						<ProgressChart
							series={data.series}
							title="Clarification progress"
						/>
					</Card>

					<div className="grid gap-6 lg:grid-cols-5">
						<div className="lg:col-span-3">
							<SectionHeading
								title="Projects"
								className="mb-3"
							/>
							{data.projects.length === 0 ? (
								<EmptyState
									icon={FolderPlus}
									title="No projects yet"
									description="A project groups one conversation - usually one job, one client, one email chain."
									action={
										<Button variant="primary" icon={FolderPlus} onClick={() => setCreating(true)}>
											Create the first project
										</Button>
									}
								/>
							) : (
								<div className="space-y-2">
									{data.projects.map((project) => (
										<ProjectRow key={project.project_id} project={project} timezone={timezone} />
									))}
								</div>
							)}
						</div>

						<div className="lg:col-span-2">
							<SectionHeading
								title="Recent activity"
								className="mb-3"
							/>
							<ActivityFeed activity={data.activity} />
						</div>
					</div>
				</>
			)}

			<NewProjectDialog open={creating} onClose={() => setCreating(false)} />
		</div>
	)
}

function ProjectRow({ project, timezone }) {
	const tone = accent(project.colour)
	return (
		<Link
			to={`/projects/${project.project_id}`}
			className={cx(
				"block rounded-card border border-ink-200/80 border-l-[3px] bg-white px-4 py-3 shadow-card transition",
				"hover:border-ink-300 hover:shadow-lifted",
				tone.rail
			)}
		>
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<p className="truncate text-[0.9375rem] font-semibold text-ink-900">
						{project.name}
					</p>
					<p className="mt-0.5 text-2xs text-ink-500">
						Updated {relativeTime(project.updated_at, timezone)}
					</p>
				</div>
				<div className="flex shrink-0 items-center gap-3 text-right">
					<span className="tabular">
						<span className="block text-base font-semibold leading-none text-state-open-ink">
							{project.open}
						</span>
						<span className="text-2xs text-ink-500">open</span>
					</span>
					<span className="tabular">
						<span className="block text-base font-semibold leading-none text-state-closed-ink">
							{project.closed}
						</span>
						<span className="text-2xs text-ink-500">resolved</span>
					</span>
				</div>
			</div>
			<SplitBar open={project.open} closed={project.closed} className="mt-2.5" />
		</Link>
	)
}

// Every event kind the feed can receive needs an entry here. Falling back to
// "raised" made an assignment look like a second, duplicate creation.
const ACTIVITY_COPY = {
	opened: { verb: "raised", icon: CircleDot, className: "text-state-open" },
	closed: { verb: "resolved", icon: CircleCheck, className: "text-state-closed" },
	reopened: { verb: "reopened", icon: RotateCcw, className: "text-state-open" },
	assigned: { verb: "assigned", icon: UserRound, className: "text-state-open" },
	unassigned: { verb: "unassigned", icon: UserMinus, className: "text-ink-400" }
}

function ActivityFeed({ activity }) {
	const { peopleById, timezone } = useApp()

	if (!activity?.length) {
		return (
			<EmptyState
				icon={CircleDot}
				title="Nothing has moved yet"
				description="Raising or resolving an issue shows up here."
			/>
		)
	}

	return (
		// Six rows then scroll. The feed is a sidebar to the projects list, not
		// the point of the page - letting it grow pushes everything else down
		// and makes the column lengths fight each other.
		<Card className="max-h-[22.5rem] divide-y divide-ink-100 overflow-y-auto scrollbar-slim">
			{activity.map((event) => {
				const copy = ACTIVITY_COPY[event.kind] || ACTIVITY_COPY.opened
				const Icon = copy.icon
				const person = peopleById[event.actor_id]
				return (
					<Link
						key={event.event_id}
						to={`/projects/${event.project_id}/issues/${event.issue_id}`}
						className="flex items-start gap-2.5 px-3.5 py-2.5 transition-colors first:rounded-t-card last:rounded-b-card hover:bg-ink-50"
					>
						<Avatar person={person} size="sm" className="mt-0.5" />
						<div className="min-w-0 flex-1">
							<p className="text-[0.8125rem] leading-snug text-ink-700">
								<span className="font-medium text-ink-900">
									{person?.name || "Someone"}
								</span>{" "}
								{copy.verb}{" "}
								{event.subject_id && (
									<span className="font-medium text-ink-900">
										{peopleById[event.subject_id]?.name || "someone"}{" "}
									</span>
								)}
								{event.subject_id && "to "}
								<span className="font-medium text-ink-900">#{event.issue_number}</span>{" "}
								<span className="text-ink-600">{event.issue_title}</span>
							</p>
							<p className="mt-0.5 flex items-center gap-1 text-2xs text-ink-400">
								<Icon size={11} className={copy.className} aria-hidden />
								{relativeTime(event.at, timezone)}
							</p>
						</div>
					</Link>
				)
			})}
		</Card>
	)
}
